import { type ApolloError, useApolloClient } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useDispatch } from 'react-redux';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import type { ActionName } from 'src/types';
import * as Schema from 'src/types/graphql';

import translations from './.vocab';
import {
  updateLanguageFailure,
  updateLanguageSuccess,
  updateLanguage,
  removeLanguageSuccess,
  removeLanguageFailure,
  removeLanguage,
} from './actionCreators';
import {
  generateDeleteLanguageOptimisticResponse,
  generateUpdateLanguageOptimisticResponse,
  updateCacheDeleteLanguage,
  updateCacheUpdateLanguage,
} from './apolloCache';

export const useLanguages = () => {
  const { data, loading, error } = useQuery(
    Schema.GetLanguageProficienciesDocument,
  );
  const languages = data?.viewer?.languageProficiencies || [];

  return {
    languages,
    loading,
    error,
  };
};

const key = 'languages';

export const useUpdateLanguage = (action: ActionName) => {
  const dispatch = useDispatch();
  const { t } = useTranslations(translations);

  const onMutate = () => {
    dispatch(updateLanguage(action));
  };
  const onCompleted = () => {
    dispatch(updateLanguageSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(updateLanguageFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.UpdateLanguageProficiencyDocument, {
    key,
    errorActionRouteParameters: { card: 'languages', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage:
      action === 'ADD' ? t('Language added') : t('Language updated'),
    update: updateCacheUpdateLanguage,
    optimisticResponse: generateUpdateLanguageOptimisticResponse(apolloClient),
  });
};

export const useRemoveLanguage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslations(translations);

  const onMutate = () => {
    dispatch(removeLanguage());
  };
  const onCompleted = () => {
    dispatch(removeLanguageSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(removeLanguageFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.DeleteLanguageProficiencyDocument, {
    key,
    errorActionRouteParameters: { card: 'languages', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Language removed'),
    update: updateCacheDeleteLanguage,
    optimisticResponse: generateDeleteLanguageOptimisticResponse(apolloClient),
  });
};
